/**
 * @fileoverview 새로운 메시지가 도착했을 때 발생하는 이벤트를 처리합니다.
 * 기존에 존재하던 채널에서 새로운 메시지가 도착하거나, 새로운 채널이 생성되며 메시지가 도착한 경우 모두 이 함수를 통해 처리합니다.
 *
 * NOTE: 새로운 메시지가 도착하면 다음과 같은 절차로 처리합니다.
 * 1. 만약 현재 선택하지 않은(화면에 보이지 않는) 채널의 메시지가 도착했다면 채널 재조정 요청을 합니다. 채널과 메시지, unread_count를 서버와 직접 동기화 합니다.
 * 2. 현재 선택한 채널의 메시지가 도착했다면 getMessagesByChannelId를 호출합니다. 이때의 반환값은 newMessageEvent로 받은 메시지입니다.
 * 3. getMessagesByChannelId를 호출해 메시지 목록을 갱신하고, markAsReadMessage를 호출해 새 메시지를 읽음 처리 합니다.
 *
 * 채널 선택 여부에 따라 나누어 처리하는 이유는 채널 목록의 CLS를 방지하기 위해서 입니다.
 * 채널 목록은 unread_count와 last_message_time을 통해 정렬되기 때문에,
 * 현재 열려있는 채널에 새 메시지가 유입되면 순간적으로 unread_count가 증가했다가 감소하면서 맨 위의 채널과 뒤바뀌는 현상이 발생합니다.
 *
 * 이를 막기위해 현재 선택한 채널의 메시지가 도착했다면 메시지 목록만을 갱신하고, channel의 unread_count에 대한 정보는 변경하지 않습니다.
 * 이렇게 하면 채널 목록의 정렬 순서는 변하지 않습니다.
 *
 * getMessagesByChannelId를 통해 메시지 목록을 갱신하는 이유는,
 * newMessage로 유입된 메시지는 Text type 이외의 메시지를 채널에 정상적으로 표시하기 위한 값이 없어 optimistic 업데이트가 불가능하기 때문입니다.
 */
import { Range } from "@daangn/idl/rocketchat/api/api";
import { isEmpty, isNil } from "lodash";
import { markAsReadMessage } from "../channel/api/markAsReadMessage";
import { createReconcileChannelThunk } from "../channel/thunks/reconcileChannelThunk";
import { getMessagesByChannelId } from "../chatMessage/api/getMessagesByChannelId";
import type { RocketchatResponseHandler } from "../RocketchatResponseHandler";
import { store } from "../redux/store";
import { SocketClient } from "../socket/socketClient";
import { activeChannelNewMessageEventThunk } from "./chatEventThunk";

export const handleNewMessageEvent: RocketchatResponseHandler = async ({
  response,
}) => {
  const newMessage = response?.new_message;

  if (isNil(newMessage)) {
    console.error("handleNewMessageResponse: message is nil");
    return;
  }

  const socket = SocketClient.getInstance().getWebSocket();

  const selectedChannelId = store.getState().channel.selectedChannelId;
  const isActiveChannel = selectedChannelId === newMessage.channel_id;

  if (!isActiveChannel) {
    const reconcileChannelThunk = createReconcileChannelThunk(
      "newMessageEventHandler",
    );
    store.dispatch(
      reconcileChannelThunk({
        channelId: newMessage.channel_id,
        criteriaMessageId: newMessage.message_id,
      }),
    );

    return;
  }

  const [messagesResponse] = await Promise.all([
    getMessagesByChannelId(
      {
        channelId: newMessage.channel_id,
        criteriaMessageId: newMessage.message_id,
        limit: 2,
        range: Range.LESS_EQUAL,
      },
      socket,
    ),
    markAsReadMessage(
      {
        channelId: newMessage.channel_id,
        messageId: newMessage.message_id,
      },
      socket,
    ),
  ]);

  if (!isEmpty(messagesResponse.messageEntitiesRecord)) {
    store.dispatch(
      activeChannelNewMessageEventThunk({
        message: messagesResponse.messageEntitiesRecord,
      }),
    );
  }
};
